import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const BrandContext = createContext(null);

const STORAGE_KEY = "kc_current_brand_id";

export function BrandProvider({ children }) {
    const { user } = useAuth();
    const [brands, setBrands] = useState([]);
    const [currentBrandId, setCurrentBrandIdState] = useState(
        () => localStorage.getItem(STORAGE_KEY) || null,
    );
    const [loaded, setLoaded] = useState(false);

    const reload = useCallback(async () => {
        try {
            const r = await api.get("/brands");
            const list = r.data || [];
            setBrands(list);
            const stored = localStorage.getItem(STORAGE_KEY);
            const valid = stored && list.find((b) => b.id === stored);
            if (!valid) {
                const def = list.find((b) => b.is_default) || list[0];
                if (def) {
                    localStorage.setItem(STORAGE_KEY, def.id);
                    setCurrentBrandIdState(def.id);
                } else {
                    localStorage.removeItem(STORAGE_KEY);
                    setCurrentBrandIdState(null);
                }
            } else {
                setCurrentBrandIdState(stored);
            }
        } catch (e) {
            // unauth or down — ignore
        } finally {
            setLoaded(true);
        }
    }, []);

    // Reload whenever auth state becomes available (login, refresh, restore).
    useEffect(() => {
        if (user) {
            reload();
        } else {
            setBrands([]);
            setLoaded(true);
        }
    }, [user, reload]);

    const setCurrentBrandId = (id) => {
        if (!id) return;
        localStorage.setItem(STORAGE_KEY, id);
        setCurrentBrandIdState(id);
        // notify listeners (pages can refresh)
        window.dispatchEvent(new CustomEvent("kc:brand-changed", { detail: { id } }));
    };

    const createBrand = async ({ name, color }) => {
        const r = await api.post("/brands", { name, color });
        await reload();
        setCurrentBrandId(r.data.id);
        return r.data;
    };

    const renameBrand = async (id, name) => {
        await api.put(`/brands/${id}`, { name });
        await reload();
    };

    const deleteBrand = async (id) => {
        await api.delete(`/brands/${id}`);
        await reload();
    };

    const setDefaultBrand = async (id) => {
        await api.post(`/brands/${id}/set-default`);
        await reload();
    };

    const currentBrand = brands.find((b) => b.id === currentBrandId);

    return (
        <BrandContext.Provider
            value={{
                brands,
                currentBrand,
                currentBrandId,
                setCurrentBrandId,
                createBrand,
                renameBrand,
                deleteBrand,
                setDefaultBrand,
                reload,
                loaded,
            }}
        >
            {children}
        </BrandContext.Provider>
    );
}

export const useBrand = () => useContext(BrandContext);
